// First define all constants at the top
const COMPLEXITY_FACTORS = {
  programCount: { threshold: 3, value: 0.2 },
  employeeCount: { threshold: 50, value: 0.15 },
  multiState: { value: 0.25 },
  international: { value: 0.3 }
};

const GROWTH_RATES = {
  conservative: { rate: 0.05, multiplier: 1.05 },
  steady: { rate: 0.10, multiplier: 1.10 },
  rapid: { rate: 0.20, multiplier: 1.20 }
};

const STATE_RATES = {
  'California': { base: 800, requirements: ['Annual RRF-1', 'SI-100', 'Charity Registration'] },
  'New York': { base: 750, requirements: ['CHAR500', 'Annual Report'] },
  'Texas': { base: 500, requirements: ['Form 802', 'Periodic Report'] },
  'Florida': { base: 600, requirements: ['Annual Report'] },
  'DEFAULT': { base: 350, requirements: ['Annual Report'] }
};

const FORM_990_COSTS = {
  base: 5500,  // Full Form 990 for revenue >= $200,000
  ez: 3500,    // Form 990-EZ for revenue < $200,000
  n: 25        // Form 990-N for revenue <= $50,000
};

// Export getForm990Cost function
export const getForm990Cost = (revenue) => {
  if (revenue <= 50000) return FORM_990_COSTS.n;
  if (revenue < 200000) return FORM_990_COSTS.ez;
  return FORM_990_COSTS.base;
};

// Then define all functions that will be exported
export const getComplexityMultiplier = (factors) => {
  let multiplier = 1.0;
  if (factors.programCount > COMPLEXITY_FACTORS.programCount.threshold) {
    multiplier += COMPLEXITY_FACTORS.programCount.value;
  }
  if (factors.employeeCount > COMPLEXITY_FACTORS.employeeCount.threshold) {
    multiplier += COMPLEXITY_FACTORS.employeeCount.value;
  }
  if (factors.hasMultipleLocations) {
    multiplier += COMPLEXITY_FACTORS.multiState.value;
  }
  if (factors.hasInternationalOperations) {
    multiplier += COMPLEXITY_FACTORS.international.value;
  }
  return multiplier;
};

export const calculateBaseStateRenewal = (state) => {
  return STATE_RATES[state]?.base || STATE_RATES.DEFAULT.base;
};

export const getStateRequirements = (state) => {
  return STATE_RATES[state]?.requirements || STATE_RATES.DEFAULT.requirements;
};

export const getStateRenewalCost = (state, revenue) => {
  const baseCost = calculateBaseStateRenewal(state);
  const revenueMultiplier = revenue > 1000000 ? 1.5 : 1.0;
  return baseCost * revenueMultiplier;
};

export const determineAuditRequirement = (revenue, state) => {
  const thresholds = {
    'California': 2000000,
    'New York': 750000,
    'DEFAULT': 500000
  };
  const threshold = thresholds[state] || thresholds.DEFAULT;
  return {
    required: revenue >= threshold,
    estimatedCost: revenue >= threshold ? 7500 + (revenue * 0.001) : 0
  };
};

export const calculateBaseStaffing = (revenue, programCount) => {
  const baseStaff = Math.ceil(revenue / 500000);
  const programStaff = Math.ceil(programCount * 1.5);
  return Math.max(baseStaff, programStaff);
};

export const getGrowthMultiplier = (growthType) => {
  return GROWTH_RATES[growthType]?.multiplier || GROWTH_RATES.steady.multiplier;
};

export const calculateStaffingCosts = (staffCount, location) => {
  const baseSalary = {
    'California': 65000,
    'New York': 70000,
    'Texas': 55000,
    'DEFAULT': 50000
  };
  const salary = baseSalary[location] || baseSalary.DEFAULT;
  return staffCount * salary;
};

export const getGrowthRate = (growthType) => {
  return GROWTH_RATES[growthType]?.rate || GROWTH_RATES.steady.rate;
};

export const getStateSpecificRisks = (state) => {
  const stateRisks = {
    'California': [
      { type: 'compliance', level: 'high', description: 'Complex reporting requirements' },
      { type: 'financial', level: 'medium', description: 'Higher operational costs' }
    ],
    'New York': [
      { type: 'compliance', level: 'medium', description: 'Regular audit requirements' },
      { type: 'financial', level: 'high', description: 'Higher insurance costs' }
    ],
    'DEFAULT': [
      { type: 'compliance', level: 'low', description: 'Standard reporting requirements' },
      { type: 'financial', level: 'low', description: 'Standard operational costs' }
    ]
  };
  return stateRisks[state] || stateRisks.DEFAULT;
};

export const categorizeAndPrioritizeRisks = (risks) => {
  return risks.map(risk => ({
    ...risk,
    priority: risk.level === 'high' ? 1 : risk.level === 'medium' ? 2 : 3,
    mitigation: suggestMitigation(risk)
  })).sort((a, b) => a.priority - b.priority);
};

export const identifyGrantOpportunities = (factors) => {
  const opportunities = [];
  if (factors.revenue < 500000) {
    opportunities.push({
      type: 'capacity',
      description: 'Small nonprofit capacity building grants',
      potential: 'high'
    });
  }
  if (factors.programCount > 3) {
    opportunities.push({
      type: 'program',
      description: 'Program diversity grants',
      potential: 'medium'
    });
  }
  return opportunities;
};

export const identifyAdditionalExemptions = (state, revenue) => {
  const exemptions = [];
  if (revenue < 50000) {
    exemptions.push('Small organization filing exemptions');
  }
  if (state === 'California' && revenue < 25000) {
    exemptions.push('CA annual filing fee exemption');
  }
  return exemptions;
};

export const identifyCostReductionOpportunities = (factors) => {
  const opportunities = [];
  if (factors.employeeCount > 20) {
    opportunities.push('Group insurance rates');
  }
  if (factors.hasMultipleLocations) {
    opportunities.push('Consolidated administrative services');
  }
  return opportunities;
};

// Helper function used by categorizeAndPrioritizeRisks
const suggestMitigation = (risk) => {
  const mitigationStrategies = {
    compliance: 'Implement compliance monitoring system',
    financial: 'Develop reserve fund',
    operational: 'Create standard operating procedures'
  };
  return mitigationStrategies[risk.type] || 'Develop risk management plan';
};